import { useEffect, useState } from 'react';
import {
  Alert,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Stack,
  TextField,
  Typography
} from '@mui/material';

import useApi from 'hooks/useApi';
import { diApi } from '../api';
import type { DataSourceSummary, SourceEntity } from '../types';
import { entityLabel, errorText, sourceTables } from './format';

type Template = 'aljex_high_value' | 'blank';

interface Props {
  open: boolean;
  clientUid: string;
  /** Passed by Overview (already loaded); fetched on open when omitted. */
  sources?: DataSourceSummary[];
  onClose: () => void;
  onCreated: (pipelineUid: string) => void;
}

export default function NewPipelineDialog({ open, clientUid, sources: given, onClose, onCreated }: Props) {
  const { showToast } = useApi();
  const [sources, setSources] = useState<DataSourceSummary[]>(given ?? []);
  const [loadingSources, setLoadingSources] = useState(false);
  const [name, setName] = useState('');
  const [sourceUid, setSourceUid] = useState('');
  const [entity, setEntity] = useState<SourceEntity>('loads');
  const [template, setTemplate] = useState<Template>('aljex_high_value');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setName('');
    setEntity('loads');
    setTemplate('aljex_high_value');
    if (given) {
      setSources(given);
      setSourceUid(given[0]?.uid ?? '');
      return;
    }
    let cancelled = false;
    setLoadingSources(true);
    diApi
      .getSources(clientUid)
      .then((res) => {
        if (cancelled) return;
        if (res.success === false) {
          showToast(res.message || 'Could not load sources', 'error');
          return;
        }
        setSources(res.data ?? []);
        setSourceUid(res.data?.[0]?.uid ?? '');
      })
      .catch((err) => !cancelled && showToast(errorText(err, 'Could not load sources'), 'error'))
      .finally(() => !cancelled && setLoadingSources(false));
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, clientUid, given]);

  // Any table the chosen source has sent can drive a pipeline.
  const tableNames = sourceTables(sources.find((s) => s.uid === sourceUid)).map((t) => t.name);
  const entityOptions = tableNames.length ? tableNames : ['loads'];
  useEffect(() => {
    if (tableNames.length && !tableNames.includes(entity)) setEntity(tableNames[0]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sourceUid, tableNames.join('|')]);

  // The high-value template is a loads pipeline; anything else starts blank.
  useEffect(() => {
    if (entity !== 'loads' && template === 'aljex_high_value') setTemplate('blank');
  }, [entity, template]);

  const canCreate = name.trim().length > 0 && Boolean(sourceUid) && !saving;

  const create = async () => {
    if (!canCreate) return;
    setSaving(true);
    try {
      const res = await diApi.createPipeline(clientUid, { name: name.trim(), sourceUid, entity, template });
      if (res.success === false || !res.data?.uid) {
        showToast(res.message || 'Could not create the pipeline', 'error');
        return;
      }
      showToast(`Created ${res.data.name || name.trim()} as a draft.`, 'success');
      onCreated(res.data.uid);
    } catch (err) {
      showToast(errorText(err, 'Could not create the pipeline'), 'error');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={saving ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle>New pipeline</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2.5}>
          <Typography variant="body2" color="text.secondary">
            A pipeline starts as a draft. It does nothing until you test it and publish it.
          </Typography>
          {!loadingSources && sources.length === 0 && (
            <Alert severity="warning">This client has no data source yet. Set up Aljex Live Sync first.</Alert>
          )}
          <TextField
            label="Name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="e.g. High Value Deliveries"
            autoFocus
            fullWidth
            inputProps={{ maxLength: 120 }}
          />
          <TextField
            select
            label="Source"
            value={sourceUid}
            onChange={(e) => setSourceUid(e.target.value)}
            fullWidth
            disabled={loadingSources || sources.length === 0}
            InputProps={loadingSources ? { endAdornment: <CircularProgress size={16} sx={{ mr: 3 }} /> } : undefined}
          >
            {sources.map((s) => (
              <MenuItem key={s.uid} value={s.uid}>
                {s.name}
                {s.tenantQual ? ` · ${s.tenantQual}` : ''}
              </MenuItem>
            ))}
          </TextField>
          <TextField select label="Records" value={entity} onChange={(e) => setEntity(e.target.value as SourceEntity)} fullWidth>
            {entityOptions.map((e) => (
              <MenuItem key={e} value={e}>
                {entityLabel(e)}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            select
            label="Start from"
            value={template}
            onChange={(e) => setTemplate(e.target.value as Template)}
            fullWidth
            helperText={
              template === 'aljex_high_value'
                ? 'Pre-filled with the high-value load filter and Job Creation mapping. Pick the target process and lookups before testing.'
                : 'An empty pipeline. You build the filter, target and mapping yourself.'
            }
          >
            <MenuItem value="aljex_high_value" disabled={entity !== 'loads'}>
              Aljex high-value loads (recommended)
            </MenuItem>
            <MenuItem value="blank">Blank</MenuItem>
          </TextField>
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} disabled={saving} color="secondary">
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={create}
          disabled={!canCreate}
          startIcon={saving ? <CircularProgress size={14} color="inherit" /> : undefined}
        >
          Create draft
        </Button>
      </DialogActions>
    </Dialog>
  );
}
